import { PageError } from "@components/ui/PageError"
import { analytics } from "@lib/analytics"
import { isChunkLoadError } from "@lib/lazyRoute"
import { logger } from "@lib/logger"
import { Component, type ErrorInfo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"

/**
 * Catches anything a route throws while loading or rendering.
 *
 * `<Suspense>` covers the waiting half of `lazy()`; nothing covered the
 * failing half, so a rejected dynamic import unmounted the whole tree and
 * left a white page (2026-08-08, /salones/:id/alumnos after a deploy).
 * `lazyRoute` handles the common stale-chunk case by reloading once — this
 * boundary is what the professor sees when that recovery has already been
 * tried, or when a route throws for some other reason entirely.
 */

function ChunkErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("common")
  return (
    <PageError
      title={t("state.chunk_error.title")}
      message={t("state.chunk_error.message")}
      onRetry={onRetry}
      retryLabel={t("actions.reload")}
    />
  )
}

function GenericErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation("common")
  return (
    <PageError
      title={t("state.route_error.title")}
      message={t("state.route_error.message")}
      onRetry={onRetry}
    />
  )
}

interface State {
  error: Error | null
}

export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("spa.route_render_failed", {
      message: error.message,
      is_chunk_error: isChunkLoadError(error),
      component_stack: info.componentStack?.slice(0, 500),
    })
    // PRD-57 FR-14 — the real `Error`, so PostHog Error Tracking gets a stack
    // it can group on. The log line above only ever carried `error.message`.
    // A render crash never reaches `window.onerror` in production builds
    // (React swallows it once a boundary handles it), so this is the only
    // place it can be reported from.
    analytics.captureException(error, {
      source: "route_error_boundary",
      handled: true,
      is_chunk_error: isChunkLoadError(error),
      component_stack: info.componentStack?.slice(0, 500),
    })
  }

  // A stale chunk can only be resolved by fetching a new document — clearing
  // the boundary would just re-run the same failing import against the same
  // index.html. Anything else is worth retrying in place first.
  private handleRetry = () => {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      window.location.reload()
      return
    }
    this.setState({ error: null })
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children
    return isChunkLoadError(error) ? (
      <ChunkErrorFallback onRetry={this.handleRetry} />
    ) : (
      <GenericErrorFallback onRetry={this.handleRetry} />
    )
  }
}
